import { useState } from 'react';
import dynamic from 'next/dynamic';
import type { MuxPlayerProps } from '@mux/mux-player-react';

import type {
  SanityVideoTwoUp,
  SanityVideoTwoUpCard,
} from '#/lib/sanity/queries/videoTwoUp';
import { createSanityFieldDataAttribute } from '#/lib/sanity/visualEditing';
import {
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
} from '#/lib/section-theme';
import type { PageLayoutSharedProps } from '#/lib/types';
import { cn, COLORS } from '#/lib/utils';
import { FormattedText } from '#/components/FormattedText';
import { Headline } from '#/components/Headline';
import { Media } from '#/components/Media';
import { Overline } from '#/components/Overline';

import { ConsentBoundary } from './ConsentBoundary';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });
const MuxPlayer = dynamic(() => import('@mux/mux-player-react'), {
  ssr: false,
});

function VideoCardOverlay({
  video,
  accentClass,
}: {
  video: SanityVideoTwoUpCard;
  accentClass: string;
}) {
  return (
    <div className="absolute inset-0 size-full">
      <Media
        media={video.thumbnailImage}
        className="size-full"
        placeholder={true}
        sizes={['100vw', { ml: '50vw', l: '600px' }]}
      />
      <div className="absolute inset-0 bg-charcoal/45" />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/35 to-charcoal/65" />
      {video.eyebrow ? (
        <p
          className={cn(
            'absolute inset-x-20 top-20 type-eyebrow m:inset-x-25 m:top-25',
            accentClass,
          )}
        >
          {video.eyebrow}
        </p>
      ) : null}
      <div className="absolute inset-x-20 bottom-20 m:inset-x-25 m:bottom-25">
        <h3 className="type-heading6 m:type-heading5 text-cloud">
          {video.title}
        </h3>
        {video.subtitle ? (
          <p className="mt-6 text-14 leading-120 text-grayscale-03 m:text-16">
            {video.subtitle}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function PlayIcon() {
  return (
    <div className="absolute left-1/2 top-1/2 grid size-64 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-cloud text-charcoal transition hover:scale-110 m:size-78">
      <svg
        className="w-24 translate-x-3 m:w-28 m:translate-x-4"
        viewBox="0 0 33 39"
        fill="none"
      >
        <path
          d="M1.25 1.74648L32 19.5L1.25 37.2535L1.25 1.74648Z"
          fill="currentColor"
        />
      </svg>
    </div>
  );
}

function YoutubeVideoCard({
  video,
  accentClass,
}: {
  video: SanityVideoTwoUpCard;
  accentClass: string;
}) {
  if (!video.videoUrl) return null;

  return (
    <ConsentBoundary urlsRequiredForRender={[video.videoUrl]}>
      <ReactPlayer
        width="100%"
        height="100%"
        url={video.videoUrl}
        className="absolute inset-0 size-full"
        controls={true}
        playsinline={true}
        playing={true}
        light={<VideoCardOverlay video={video} accentClass={accentClass} />}
        playIcon={<PlayIcon />}
      />
    </ConsentBoundary>
  );
}

function MuxVideoCard({
  video,
  accentClass,
}: {
  video: SanityVideoTwoUpCard;
  accentClass: string;
}) {
  const [playing, setPlaying] = useState(false);
  const playbackId = video.playbackId ?? undefined;

  if (!playbackId) return null;

  const streamUrl = `https://stream.mux.com/${playbackId}.m3u8`;

  return (
    <ConsentBoundary urlsRequiredForRender={[streamUrl]}>
      {playing ? (
        <MuxPlayer
          className="absolute inset-0 size-full"
          style={
            {
              '--media-object-fit': 'contain',
              '--media-object-position': 'center',
              width: '100%',
              height: '100%',
            } satisfies MuxPlayerProps['style']
          }
          playbackId={playbackId}
          streamType="on-demand"
          autoPlay
          playsInline
          primaryColor={COLORS.cloud}
          secondaryColor={COLORS.charcoal}
          accentColor={COLORS.cipher.DEFAULT}
        />
      ) : (
        <button
          type="button"
          className="absolute inset-0 size-full cursor-pointer"
          onClick={() => setPlaying(true)}
          aria-label={`Play ${video.title}`}
        >
          <VideoCardOverlay video={video} accentClass={accentClass} />
          <PlayIcon />
        </button>
      )}
    </ConsentBoundary>
  );
}

function VideoCard({
  video,
  accentClass,
}: {
  video: SanityVideoTwoUpCard;
  accentClass: string;
}) {
  return (
    <div className="relative aspect-[16/9] overflow-clip rounded-15 bg-charcoal m:rounded-25">
      {video.mediaType === 'mux' ? (
        <MuxVideoCard video={video} accentClass={accentClass} />
      ) : (
        <YoutubeVideoCard video={video} accentClass={accentClass} />
      )}
    </div>
  );
}

export function VideoTwoUp(props: SanityVideoTwoUp & PageLayoutSharedProps) {
  const isFirst = props.sectionIndex === 0;
  const isDark = isDarkSectionTheme(props.theme);
  const accentClass = getSectionThemeAccent(props.theme);
  const hasHeader = Boolean(
    props.overline || props.title || props.description?.length,
  );

  return (
    <div
      className={cn('p-main', getSectionThemeSurface(props.theme), {
        'py-60 m:py-100': !isFirst,
        'mt-[calc(var(--header-height)*-1)] pb-60 m:pb-100 pt-[calc(var(--header-height)+5rem)]':
          isFirst,
      })}
    >
      <div className="mx-auto max-w-1235">
        {hasHeader ? (
          <div className="mb-30 max-w-735 m:mb-50">
            {props.title ? (
              <Headline
                value={props.title}
                overline={props.overline}
                overlineClassName={cn('type-eyebrow', accentClass)}
                variant={4}
                highlightClass={accentClass}
                className={
                  props.description?.length ? 'mb-15 m:mb-20' : undefined
                }
                dataSanity={createSanityFieldDataAttribute(
                  props.sanitySource,
                  'title',
                )}
              />
            ) : props.overline ? (
              <Overline
                className={cn(
                  'type-eyebrow',
                  accentClass,
                  props.description?.length ? 'mb-15 m:mb-20' : undefined,
                )}
                dataSanity={createSanityFieldDataAttribute(
                  props.sanitySource,
                  'overline',
                )}
              >
                {props.overline}
              </Overline>
            ) : null}
            {props.description?.length ? (
              <FormattedText
                value={props.description}
                className={isDark ? 'text-body-cloud' : 'text-body'}
                dataSanity={createSanityFieldDataAttribute(
                  props.sanitySource,
                  'description',
                )}
              />
            ) : null}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-20 ml:grid-cols-2 ml:gap-25">
          {props.videos.map((video) => (
            <VideoCard
              key={video._key}
              video={video}
              accentClass={accentClass}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
